import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ExternalLink, ImageOff, MapPin, Radar as RadarIcon, RefreshCw } from 'lucide-react';
import { firstImage, useRadarListings, useRadarStats, type RadarListingRow } from '@/hooks/data/useRadar';
import { formatNumber, formatRelative } from '@/lib/formatLocale';
import { toMs } from '@/lib/appointmentDate';

export const Route = createFileRoute('/_app/radar')({ component: RadarPage });

/** Where a candidate came from, always visible. These are foreign
 *  advertisements, and a row whose origin is not obvious is a row somebody
 *  will eventually mistake for a 4M object. */
const SOURCE_META: Record<string, { label: string; site: string; cls: string }> = {
  keinmakler: {
    label: 'keinmakler.com',
    site: 'https://www.keinmakler.com',
    cls: 'bg-[hsl(var(--brand-gold)/0.14)] text-[hsl(var(--brand-gold-deep))] border-[hsl(var(--brand-gold)/0.35)]',
  },
  privatimmobilien: {
    label: 'privatimmobilien.at',
    site: 'https://www.privatimmobilien.at',
    cls: 'bg-[hsl(var(--info)/0.12)] text-[hsl(var(--info))] border-[hsl(var(--info)/0.3)]',
  },
  bazar: {
    label: 'bazar.at',
    site: 'https://www.bazar.at',
    cls: 'bg-[hsl(var(--positive)/0.12)] text-[hsl(var(--positive))] border-[hsl(var(--positive)/0.3)]',
  },
  // E-mail-ingested portals — the saved-search alert mails forwarded from
  // Gmail. The "· E-Mail" suffix makes the origin unmistakable (we do not
  // scrape these portals).
  email_willhaben: {
    label: 'willhaben · E-Mail',
    site: 'https://www.willhaben.at',
    cls: 'bg-[hsl(var(--warning)/0.14)] text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.35)]',
  },
  email_immoscout24: {
    label: 'ImmoScout24 · E-Mail',
    site: 'https://www.immobilienscout24.at',
    cls: 'bg-[hsl(var(--info)/0.12)] text-[hsl(var(--info))] border-[hsl(var(--info)/0.3)]',
  },
  email_derstandard: {
    label: 'derStandard · E-Mail',
    site: 'https://immobilien.derstandard.at',
    cls: 'bg-[hsl(var(--brand-gold)/0.14)] text-[hsl(var(--brand-gold-deep))] border-[hsl(var(--brand-gold)/0.35)]',
  },
};

function sourceMeta(id: string) {
  return (
    SOURCE_META[id] ?? {
      label: id,
      site: '',
      cls: 'bg-paper-2 text-ink-2 border-line',
    }
  );
}

const DAY_MS = 24 * 60 * 60 * 1000;

function RadarPage() {
  const { t } = useTranslation();
  const [source, setSource] = useState('');
  // No default narrowing: the list opens on everything the radar holds.
  // A view that starts filtered looks like a smaller database (batch BR).
  const [deal, setDeal] = useState('');
  const [wien, setWien] = useState(false);
  const [search, setSearch] = useState('');
  const [priceMax, setPriceMax] = useState('');

  const filters = useMemo(
    () => ({
      source: source || undefined,
      deal: deal || undefined,
      wien,
      priceMax: priceMax ? Number(priceMax.replace(/\D/g, '')) : null,
      search: search.trim() || undefined,
      limit: 90,
    }),
    [source, deal, wien, priceMax, search],
  );

  const { data, isLoading, isFetching, refetch } = useRadarListings(filters);
  const { data: stats } = useRadarStats();

  // Counts cover what the page shows: wanted ads and everything outside
  // Vienna and its surroundings are left out, and the second number says how
  // many were left out for being elsewhere — a narrowed view must say so.
  const { perSource, outside } = useMemo(() => {
    const out = new Map<string, number>();
    let away = 0;
    for (const b of stats?.buckets ?? []) {
      if (b.status === 'out_of_region') {
        away += b.n;
        continue;
      }
      if (b.status === 'dismissed') continue;
      out.set(b.source, (out.get(b.source) ?? 0) + b.n);
    }
    return { perSource: out, outside: away };
  }, [stats]);

  const total = [...perSource.values()].reduce((a, b) => a + b, 0);
  const items = data?.items ?? [];

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-2xl text-ink flex items-center gap-2">
            <RadarIcon className="h-5 w-5 text-[hsl(var(--brand-gold-deep))]" />
            {t('radar.title', 'Radar — Objekte ohne Makler')}
          </h3>
          <p className="text-sm text-ink-3">
            {t(
              'radar.subtitle',
              'Privatangebote aus Wien und Umgebung, neueste zuerst. Alle 30 Minuten automatisch geprüft — ein Klick öffnet das Original-Inserat.',
            )}
          </p>
          {outside > 0 && (
            <p className="mt-1 text-xs text-ink-3">
              {t(
                'radar.outside_hidden',
                '{{n}} Angebote außerhalb von Wien und Umgebung werden nicht angezeigt.',
                { n: formatNumber(outside) },
              )}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => void refetch()}
          className="inline-flex items-center gap-2 rounded-md border border-line bg-card px-3 py-2 text-sm text-ink-2 hover:bg-paper-2"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          {t('common.refresh', 'Aktualisieren')}
        </button>
      </header>

      {/* Per-source counts double as the origin legend. */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <button
          type="button"
          onClick={() => setSource('')}
          className={`fm-kpi rounded-xl border p-3 text-start ${
            source === '' ? 'border-ink' : 'border-line'
          }`}
        >
          <p className="text-xs uppercase tracking-[0.08em] text-ink-3">
            {t('radar.all_sources', 'Alle Quellen')}
          </p>
          <p className="font-display text-2xl text-ink">{formatNumber(total)}</p>
        </button>
        {[...perSource.entries()].map(([id, n]) => {
          const meta = sourceMeta(id);
          return (
            <button
              key={id}
              type="button"
              onClick={() => setSource(source === id ? '' : id)}
              className={`fm-kpi rounded-xl border p-3 text-start ${
                source === id ? 'border-ink' : 'border-line'
              }`}
            >
              <p className="truncate text-xs uppercase tracking-[0.08em] text-ink-3">
                {meta.label}
              </p>
              <p className="font-display text-2xl text-ink">{formatNumber(n)}</p>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex overflow-hidden rounded-md border border-line">
          {([
            ['', t('common.all', 'Alle')],
            ['kauf', t('radar.deal.kauf', 'Kauf')],
            ['miete', t('radar.deal.miete', 'Miete')],
          ] as Array<[string, string]>).map(([v, label]) => (
            <button
              key={v}
              type="button"
              onClick={() => setDeal(v)}
              className={`px-3 py-2 text-sm ${
                deal === v ? 'bg-ink text-paper' : 'bg-card text-ink-2 hover:bg-paper-2'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setWien((w) => !w)}
          className={`rounded-md border px-3 py-2 text-sm ${
            wien ? 'border-ink bg-ink text-paper' : 'border-line bg-card text-ink-2 hover:bg-paper-2'
          }`}
        >
          {t('radar.only_vienna', 'Nur Wien')}
        </button>
        <input
          value={priceMax}
          onChange={(e) => setPriceMax(e.target.value)}
          inputMode="numeric"
          placeholder={t('radar.price_max', 'Preis bis €')}
          className="w-36 rounded-md border border-line bg-card px-3 py-2 text-sm"
        />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('radar.search', 'Adresse, PLZ, Titel …')}
          className="min-w-[200px] flex-1 rounded-md border border-line bg-card px-3 py-2 text-sm"
        />
      </div>

      {isLoading ? (
        <p className="text-sm text-ink-3">{t('common.loading', 'Lädt …')}</p>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-line bg-card p-8 text-center">
          <p className="text-ink-2">
            {t('radar.empty', 'Kein Treffer mit diesen Filtern.')}
          </p>
          <p className="mt-1 text-sm text-ink-3">
            {t('radar.empty_hint', 'Insgesamt sind {{n}} Angebote im Radar.', { n: total })}
          </p>
        </div>
      ) : (
        <>
          <p className="text-xs text-ink-3">
            {t('radar.count', '{{shown}} von {{total}} angezeigt', {
              shown: items.length,
              total: data?.total ?? items.length,
            })}
            {' · '}
            {t('radar.sorted_newest', 'Neueste Funde zuerst')}
          </p>
          <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {items.map((row) => (
              <RadarCard key={row.id} row={row} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function RadarCard({ row }: { row: RadarListingRow }) {
  const { t } = useTranslation();
  const meta = sourceMeta(row.source);
  const img = firstImage(row);
  const place = [row.postal_code, row.city].filter(Boolean).join(' ');
  const addr = [row.street, place].filter(Boolean).join(', ');
  // timestamp_ms columns arrive as ISO strings, never as numbers — the
  // documented pitfall that has emptied four screens in this project.
  const foundMs = toMs(row.first_seen_at);
  const isFresh = Number.isFinite(foundMs) && Date.now() - foundMs < DAY_MS;

  // The whole card is the link. The listing lives on the portal; the CRM only
  // points at it, so there is nothing else a click could reasonably mean.
  return (
    <li>
      <a
        href={row.source_url}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="fm-card flex h-full flex-col overflow-hidden rounded-xl border border-line bg-card transition hover:border-ink/40"
      >
        <div className="relative h-40 w-full bg-paper-2">
          {img ? (
            // Portal-hosted image, shown as it is served. Copying it into R2
            // would mean storing somebody else's photo of somebody else's flat.
            <img
              src={img}
              alt=""
              loading="lazy"
              referrerPolicy="no-referrer"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-ink-3">
              <ImageOff className="h-6 w-6" />
            </div>
          )}
          <span
            className={`absolute start-2 top-2 rounded-md border px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}
          >
            {meta.label}
          </span>
          <span className="absolute end-2 top-2 flex items-center gap-1">
            {isFresh && (
              <span className="rounded-md bg-[hsl(var(--brand-gold))] px-2 py-0.5 text-[11px] font-semibold text-ink">
                {t('radar.badge_new', 'Neu')}
              </span>
            )}
            {row.deal_type && (
              <span className="rounded-md border border-line bg-card/90 px-2 py-0.5 text-[11px] text-ink-2">
                {row.deal_type === 'kauf'
                  ? t('radar.deal.kauf', 'Kauf')
                  : t('radar.deal.miete', 'Miete')}
              </span>
            )}
          </span>
        </div>

        <div className="flex flex-1 flex-col gap-2 p-3">
          <p className="line-clamp-2 text-sm font-medium text-ink">{row.title ?? '—'}</p>

          <p className="inline-flex items-center gap-1 text-xs text-ink-3">
            <MapPin className="h-3 w-3" />
            {addr || '—'}
          </p>

          <div className="mt-auto flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-display text-lg text-ink">
              {row.price_eur != null
                ? `€ ${formatNumber(row.price_eur, { maximumFractionDigits: 0 })}`
                : t('radar.price_hidden', 'Preis auf Anfrage')}
            </span>
            {row.size_m2 != null && (
              <span className="text-sm text-ink-2">{row.size_m2} m²</span>
            )}
            {row.rooms != null && (
              <span className="text-sm text-ink-2">
                {row.rooms} {t('radar.rooms', 'Zi.')}
              </span>
            )}
          </div>

          {/* Says out loud what the order of the list means. */}
          <p className="text-[11px] text-ink-3">
            {t('radar.found', 'Gefunden {{when}}', { when: formatRelative(row.first_seen_at) })}
          </p>

          <span className="mt-1 inline-flex items-center justify-center gap-2 rounded-md border border-line bg-paper-2 px-3 py-2 text-sm text-ink-2">
            <ExternalLink className="h-3.5 w-3.5" />
            {t('radar.open_at_portal', 'Auf {{portal}} öffnen', { portal: meta.label })}
          </span>
        </div>
      </a>
    </li>
  );
}
